/**
 * 长文本左右对比（只读；PR6 新写，判定卡 09 差异 #3、线框 03 §二）。
 * 判定卡写的是「admin 已有的 Monaco 对比编辑器」🟡；admin 的 Monaco 运行时从 CDN 取（没配本地 loader），离线 ／ e2e 下出不来，
 * 这里改用按行比对的轻量件：左「前」右「后」，删掉的行标红、加的行标绿，连续相同的行收成「… N 行相同 …」（前后各留 2 行）。
 */
import { cn } from "@/lib/utils";

type Op = { kind: "same" | "del" | "add"; text: string };

/** 按行 LCS（提示词一般几十～一两百行，O(n·m) 足够）；dp 用一维数组，下标 i*(m+1)+j */
export function lineDiff(before: string, after: string): Op[] {
	const a = before.split("\n");
	const b = after.split("\n");
	const n = a.length;
	const m = b.length;
	const w = m + 1;
	const dp = new Int32Array((n + 1) * w);
	const at = (i: number, j: number) => dp[i * w + j] ?? 0;
	for (let i = n - 1; i >= 0; i--)
		for (let j = m - 1; j >= 0; j--)
			dp[i * w + j] = a[i] === b[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
	const out: Op[] = [];
	let i = 0;
	let j = 0;
	while (i < n && j < m) {
		const x = a[i] ?? "";
		const y = b[j] ?? "";
		if (x === y) {
			out.push({ kind: "same", text: x });
			i++;
			j++;
		} else if (at(i + 1, j) >= at(i, j + 1)) {
			out.push({ kind: "del", text: x });
			i++;
		} else {
			out.push({ kind: "add", text: y });
			j++;
		}
	}
	for (; i < n; i++) out.push({ kind: "del", text: a[i] ?? "" });
	for (; j < m; j++) out.push({ kind: "add", text: b[j] ?? "" });
	return out;
}

type Pair = { kind: "pair"; left: Op | null; right: Op | null };
type Row = Pair | { kind: "fold"; count: number };

const isSame = (p: Pair | undefined) => p?.left?.kind === "same";

/** 把操作序列排成左右两栏的行；删 ／ 加相邻时并排对齐；长段相同行收起 */
function toRows(ops: Op[], context = 2): Row[] {
	const pairs: Pair[] = [];
	let k = 0;
	while (k < ops.length) {
		const op = ops[k];
		if (op?.kind === "same") {
			pairs.push({ kind: "pair", left: op, right: op });
			k++;
			continue;
		}
		const dels: Op[] = [];
		const adds: Op[] = [];
		for (let o = ops[k]; o && o.kind !== "same"; o = ops[++k]) {
			if (o.kind === "del") dels.push(o);
			else adds.push(o);
		}
		for (let x = 0; x < Math.max(dels.length, adds.length); x++)
			pairs.push({ kind: "pair", left: dels[x] ?? null, right: adds[x] ?? null });
	}
	const out: Row[] = [];
	let s = 0;
	while (s < pairs.length) {
		if (!isSame(pairs[s])) {
			const p = pairs[s];
			if (p) out.push(p);
			s++;
			continue;
		}
		let e = s;
		while (e < pairs.length && isSame(pairs[e])) e++;
		const len = e - s;
		const head = s === 0 ? 0 : context;
		const tail = e === pairs.length ? 0 : context;
		if (len > head + tail + 1) {
			out.push(...pairs.slice(s, s + head));
			out.push({ kind: "fold", count: len - head - tail });
			out.push(...pairs.slice(e - tail, e));
		} else out.push(...pairs.slice(s, e));
		s = e;
	}
	return out;
}

function Cell({ op, side }: { op: Op | null; side: "left" | "right" }) {
	if (!op) return <div className="min-h-5 bg-surface-subtle/60" />;
	const changed = op.kind !== "same";
	return (
		<div
			className={cn(
				"min-h-5 whitespace-pre-wrap break-all px-2",
				changed && side === "left" ? "bg-brand-danger/10 text-text-primary" : "",
				changed && side === "right" ? "bg-brand-success/10 text-text-primary" : "",
				!changed ? "text-text-secondary" : "",
			)}
			data-diff={op.kind}
		>
			<span className="select-none pr-1 text-text-muted">
				{op.kind === "del" ? "-" : op.kind === "add" ? "+" : " "}
			</span>
			{op.text}
		</div>
	);
}

export function TextDiff({
	before,
	after,
	beforeLabel,
	afterLabel,
}: {
	before: string;
	after: string;
	beforeLabel: string;
	afterLabel: string;
}) {
	const rows = toRows(lineDiff(before, after));
	return (
		<div
			className="overflow-hidden rounded-lg border border-surface-border"
			data-testid="plan-text-diff"
		>
			<div className="grid grid-cols-2 border-b border-surface-border bg-surface-muted text-xs text-text-secondary">
				<div className="px-2 py-1.5">前（{beforeLabel}）</div>
				<div className="border-l border-surface-border px-2 py-1.5">后（{afterLabel}）</div>
			</div>
			<div className="max-h-[60vh] overflow-y-auto font-mono text-xs leading-5">
				{rows.map((r, idx) =>
					r.kind === "fold" ? (
						<div
							// biome-ignore lint/suspicious/noArrayIndexKey: 行顺序固定、只读
							key={idx}
							className="bg-surface-subtle px-2 text-center text-text-muted"
						>
							… {r.count} 行相同 …
						</div>
					) : (
						// biome-ignore lint/suspicious/noArrayIndexKey: 行顺序固定、只读
						<div key={idx} className="grid grid-cols-2">
							<Cell op={r.left} side="left" />
							<div className="border-l border-surface-border">
								<Cell op={r.right} side="right" />
							</div>
						</div>
					),
				)}
			</div>
		</div>
	);
}
